import {
    FC,
    ReactElement,
    useMemo,
    useState,
} from 'react';

import { useDebounce } from 'react-use';

import { debounceDelay, longDebounceDelay } from '../../../constants';
import { JobExperiencesCard } from '../../../containers';
import { transformOccupationToSearchableOption } from '../../../models/@competentNL/Occupations/OccupationsTransformers';
import { SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { JobExperience, JobExperienceFormData } from '../../../models/JobExperience/JobExperience';
import { setSkillsIsSuccessful as setSkillsStepIsSuccessful } from '../../../redux/competentNL/competentNL';
import { fetchCompetentNLOccupation, fetchCompetentNLOccupations, fetchCompetentNLSkills } from '../../../redux/competentNL/competentNLActions';
import {
    setCreateIsSuccessful as setJobExperienceCreateIsSuccessful,
    setUpdateIsSuccessful as setJobExperienceUpdateIsSuccessful,
} from '../../../redux/jobExperience/jobExperience';
import { addJobExperience, deleteJobExperience, editJobExperience } from '../../../redux/jobExperience/jobExperienceActions';
import { updateCandidateSkills } from '../../../redux/skills/skillsActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';
import { SearchableOption } from '../../../types';

interface ConnectedJobExperiencesCardProps {
    className?: string;
}

const ConnectedJobExperiencesCard: FC<ConnectedJobExperiencesCardProps> = ({
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();

    const candidate = useTypedSelector(state => state.candidateReducer.candidate);
    const user = useTypedSelector(state => state.userReducer.user);

    const skillsPassportIsLoading = useTypedSelector(state => state.skillsPassportReducer.isLoading);
    const skillsPassportError = useTypedSelector(state => state.skillsPassportReducer.error);
    const jobExperiences = useTypedSelector(state => state.skillsPassportReducer.jobExperiences);
    const skills = useTypedSelector(state => state.skillsPassportReducer.careerSkills);

    const jobExperienceIsLoading = useTypedSelector(state => state.jobExperienceReducer.isLoading);
    const jobExperienceCreateIsSuccessful = useTypedSelector(state => state.jobExperienceReducer.createIsSuccessful);
    const jobExperienceUpdateIsSuccessful = useTypedSelector(state => state.jobExperienceReducer.updateIsSuccessful);
    const jobExperienceError = useTypedSelector(state => state.jobExperienceReducer.error);

    const occupations = useTypedSelector(state => state.competentNLReducer.occupations);
    const occupation = useTypedSelector(state => state.competentNLReducer.occupation);
    const skillsIsLoading = useTypedSelector(state => state.competentNLReducer.skillsIsLoading);
    const skillsStepIsSuccessful = useTypedSelector(state => state.competentNLReducer.skillsIsSuccessful);
    const skillBases = useTypedSelector(state => state.competentNLReducer.skillBases);
    const skillsError = useTypedSelector(state => state.competentNLReducer.skillsError);

    const [occupationQuery, setOccupationQuery] = useState<string>('');
    const [skillQuery, setSkillQuery] = useState<string>('');

    const occupationOptions = useMemo((): SearchableOption[] => (
        occupations.map(transformOccupationToSearchableOption)
    ), [occupations]);

    const occupationSkillOptions = useMemo((): SkillBase[] => {
        if (!occupation) {
            return skillBases;
        }

        const { essentialSkills, importantSkills, extraSkills } = occupation;

        return [...essentialSkills, ...importantSkills, ...extraSkills];
    }, [occupation]);

    useDebounce((): void => {
        if (occupationQuery) {
            dispatch(fetchCompetentNLOccupations(occupationQuery));
        }
    }, debounceDelay, [occupationQuery]);

    useDebounce((): void => {
        if (skillQuery.length > 1) {
            dispatch(fetchCompetentNLSkills(skillQuery));
        }
    }, longDebounceDelay, [skillQuery]);

    const handleSelectOccupation = (occupationId: string): void => {
        dispatch(fetchCompetentNLOccupation(occupationId));
    };

    const handleAddJobExperience = (jobExperienceFormData: JobExperienceFormData): void => {
        if (candidate?.uuid) dispatch(addJobExperience(jobExperienceFormData, candidate.uuid));
    };

    const handleEditJobExperience = (jobExperienceFormData: JobExperienceFormData): void => {
        if (candidate?.uuid) dispatch(editJobExperience(jobExperienceFormData, candidate.uuid));
    };

    const handleDeleteJobExperience = (jobExperience: JobExperience): void => {
        if (candidate?.uuid) dispatch(deleteJobExperience(jobExperience, candidate.uuid));
    };

    const handleAddSkills = (skillsFormData: SkillsFormData): void => {
        if (candidate?.uuid) {
            dispatch(updateCandidateSkills(skillsFormData, candidate.uuid));
        }
    };

    const handleIsSuccessfulReset = (): void => {
        dispatch(setJobExperienceCreateIsSuccessful(false));
        dispatch(setJobExperienceUpdateIsSuccessful(false));
        dispatch(setSkillsStepIsSuccessful(false));
    };

    return (
        <JobExperiencesCard
            isLoading={skillsPassportIsLoading}
            jobExperienceIsLoading={jobExperienceIsLoading}
            jobExperienceCreateIsSuccessful={jobExperienceCreateIsSuccessful}
            jobExperienceUpdateIsSuccessful={jobExperienceUpdateIsSuccessful}
            skillsIsLoading={skillsIsLoading}
            skillsStepIsSuccessful={skillsStepIsSuccessful}
            hasError={!!skillsPassportError}
            jobExperienceError={jobExperienceError}
            skillOptionsError={skillsError}
            userRoles={user?.roles}
            jobExperiences={jobExperiences}
            skills={skills}
            occupationOptions={occupationOptions}
            skillOptions={occupationSkillOptions}
            searchedSkillOptions={skillBases}
            onOccupationInput={setOccupationQuery}
            onSelectOccupation={handleSelectOccupation}
            onAddJobExperience={handleAddJobExperience}
            onEditJobExperience={handleEditJobExperience}
            onDeleteJobExperience={handleDeleteJobExperience}
            onSearchSkills={setSkillQuery}
            onAddSkills={handleAddSkills}
            onIsSuccessfulReset={handleIsSuccessfulReset}
            className={className}
        />
    );
};

export default ConnectedJobExperiencesCard;
